import * as Sentry from "@sentry/react";
import { Button, LoadingShim } from "@narmi/design_system";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { Suspense, type ReactNode } from "react";

/**
 * Isolates a section of the Messages UI that fetches its own data.
 *
 * Every data hook in this feature uses `useSuspenseQuery`, which *throws* on
 * failure during render. `<Suspense>` only handles the pending promise, not the
 * error — so without a component-level error boundary the throw propagates all
 * the way to the router's `errorElement` and replaces the entire page. That
 * means a single failing sidebar request (e.g. `/api/user_notes/`) blanks the
 * whole Messages screen.
 *
 * Wrapping each independently-fetching section in this boundary keeps a failure
 * scoped to that section, while `QueryErrorResetBoundary` lets "Try again"
 * actually re-run the failed query instead of re-throwing the cached error.
 */
export function PanelBoundary({
  children,
  label,
  fallback = <LoadingShim isLoading />,
}: {
  children: ReactNode;
  label: string;
  fallback?: ReactNode;
}) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <Sentry.ErrorBoundary
          onReset={reset}
          fallback={({ resetError }) => (
            <div className="padding--all--m">
              <div className="fontColor--secondary margin--bottom--xs">Unable to load {label}.</div>
              <Button kind="plain" label="Try again" onClick={resetError} />
            </div>
          )}
        >
          <Suspense fallback={fallback}>{children}</Suspense>
        </Sentry.ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
